import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';

import { useTranslation } from '@splitfiesta/i18n';
import {
  formatSignedCurrencyWithCode,
  isEffectivelyZero,
} from '@splitfiesta/shared';

import { useUserNetBalanceQuery } from '@/hooks/queries/useBalances';

export interface BalanceLine {
  currency: string;
  amount: number;
}

/**
 * Keep only currencies whose net balance is not effectively zero. A
 * settled currency (you owe nothing, nobody owes you) is dropped, so
 * an all-settled user produces an empty list and the card hides.
 */
export function deriveBalanceLines(
  totalsByCurrency: Record<string, number> | undefined
): BalanceLine[] {
  if (!totalsByCurrency) return [];
  return Object.entries(totalsByCurrency)
    .filter(([currency, amount]) => !isEffectivelyZero(amount, currency))
    .map(([currency, amount]) => ({ currency, amount }));
}

/**
 * Aggregated net balance across all the user's groups — one line per
 * non-settled currency. A direction icon sits on the left with the
 * amount beside it (tabular figures). Renders nothing when everything
 * is settled.
 */
export function GlobalBalanceCard() {
  const { t } = useTranslation();
  const { data } = useUserNetBalanceQuery();
  const lines = deriveBalanceLines(data?.totalsByCurrency);

  if (lines.length === 0) return null;

  return (
    <div
      data-testid="global-balance-card"
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 dark:border-gray-700 dark:bg-gray-800"
    >
      <h2 className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400">
        {t('dashboard:globalBalances.title')}
      </h2>
      <div className="space-y-2">
        {lines.map(({ currency, amount }) => {
          const isPositive = amount > 0;
          const colorClass = isPositive
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';
          // Icon sits close to the row's tinted background — a pale shade
          // in light, a dark muted shade in dark — so it reads as part of
          // the container rather than a high-contrast mark.
          const iconColorClass = isPositive
            ? 'text-green-300 dark:text-green-800'
            : 'text-red-300 dark:text-red-800';
          const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;
          return (
            <div
              key={currency}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                isPositive
                  ? 'bg-green-50 dark:bg-green-500/10'
                  : 'bg-red-50 dark:bg-red-500/10'
              }`}
              data-testid={`global-balance-line-${currency}`}
            >
              <Icon className={`h-8 w-8 shrink-0 ${iconColorClass}`} />
              <div>
                <p className={`text-2xl font-bold tabular-nums ${colorClass}`}>
                  {formatSignedCurrencyWithCode(amount, currency)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isPositive
                    ? t('common:labels.youAreOwed')
                    : t('common:labels.youOwe')}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
